import PaginationMui from "@app/components/pagination-mui/pagination-mui.component";
import { DEFAULT_PAGE, OrderType } from "@app/constants";
import CardReceiveIcon from "@app/icons/dashboard/card-receive";
import DashboardService from "@app/services/http/dashboard.service";
import { Meta, StatisticsState } from "@app/types";
import {
  BookRevenue as BookRevenueType,
  RequestParamsDashboard,
} from "@app/types/dashboard.type";
import { Images } from "@assets/images";
import clsx from "clsx";
import { memo, useEffect, useState } from "react";
import * as XLSX from "xlsx";

const retailHeaderTitle = [
  {
    label: "TÁC GIẢ",
    key: "authorName",
    hasOrder: true,
  },
  {
    label: "TÊN SÁCH",
    key: "bookName",
    hasOrder: true,
  },
  {
    label: "LƯỢT MUA",
    key: "purchases",
    hasOrder: true,
  },
  {
    label: "SỐ TIỀN THU ĐƯỢC",
    key: "money",
    hasOrder: true,
  },
  {
    label: "SỐ TIỀN (all)",
    key: "moneyAllTime",
    hasOrder: false,
  },
];

const vipHeaderTitle = [
  {
    label: "TÁC GIẢ",
    key: "authorName",
    hasOrder: true,
  },
  {
    label: "TÊN SÁCH",
    key: "bookName",
    hasOrder: true,
  },
  {
    label: "LƯỢT XEM",
    key: "listens",
    hasOrder: true,
  },
  {
    label: "LƯỢT XEM (all)",
    key: "listensAllTime",
    hasOrder: false,
  },
  {
    label: "SỐ TIỀN THU ĐƯỢC",
    key: "money",
    hasOrder: true,
  },
];

const BookRevenue = ({ filter }: { filter: StatisticsState }) => {
  const [retailData, setRetailData] = useState<BookRevenueType>();
  const [retailMeta, setRetailMeta] = useState<Meta>();
  const [retailRequestParams, setRetailRequestParams] =
    useState<RequestParamsDashboard>({
      pageNum: DEFAULT_PAGE,
      pageSize: 20,
    });
  const [retailOrderBy, setRetailOrderBy] = useState("");
  const [retailOrder, setRetailOrder] = useState("");

  const [vipData, setVipData] = useState<BookRevenueType>();
  const [vipMeta, setVipMeta] = useState<Meta>();
  const [vipRequestParams, setVipRequestParams] =
    useState<RequestParamsDashboard>({
      pageNum: DEFAULT_PAGE,
      pageSize: 20,
    });
  const [vipOrderBy, setVipOrderBy] = useState("");
  const [vipOrder, setVipOrder] = useState("");

  useEffect(() => {
    DashboardService.getBookRevenueRetail({
      ...retailRequestParams,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
    }).then((res) => {
      setRetailData(res.data);
      setRetailMeta(res.data.meta);
    });
  }, [retailRequestParams, filter.startDate, filter.endDate]);

  useEffect(() => {
    DashboardService.getBookRevenueVip({
      ...vipRequestParams,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
    }).then((res) => {
      setVipData(res.data);
      setVipMeta(res.data.meta);
    });
  }, [vipRequestParams, filter.startDate, filter.endDate]);

  useEffect(() => {
    const newRequestParams: RequestParamsDashboard = {
      ...retailRequestParams,
      sortBy: retailOrderBy,
      sortType: retailOrder as OrderType,
    };

    setRetailRequestParams(newRequestParams);
  }, [retailOrderBy, retailOrder]);

  useEffect(() => {
    const newRequestParams: RequestParamsDashboard = {
      ...vipRequestParams,
      sortBy: vipOrderBy,
      sortType: vipOrder as OrderType,
    };

    setVipRequestParams(newRequestParams);
  }, [vipOrderBy, vipOrder]);

  const onRetailPageChange = (page: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...retailRequestParams,
      pageNum: page,
    };

    setRetailRequestParams(newRequestParams);
  };

  const onVipPageChange = (page: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...vipRequestParams,
      pageNum: page,
    };

    setVipRequestParams(newRequestParams);
  };

  const onRetailPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...retailRequestParams,
      pageSize,
    };

    setRetailRequestParams(newRequestParams);
  };

  const onVipPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...vipRequestParams,
      pageSize,
    };

    setVipRequestParams(newRequestParams);
  };

  const handleExportToExcelRetail = () => {
    if (!retailData?.items) return;

    const excelData = [
      ["TÁC GIẢ", "TÊN SÁCH", "LƯỢT MUA", "SỐ TIỀN THU ĐƯỢC"],
      // eslint-disable-next-line no-unsafe-optional-chaining
      ...retailData?.items.map((item) => [
        item.authorName,
        item.bookName,
        item.purchases ?? 0,
        `${(item.money ?? 0).toLocaleString("de-DE")}đ`,
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách");

    XLSX.writeFile(workbook, "Sach_ban_le.xlsx");
  };

  const handleExportToExcelVip = () => {
    if (!vipData?.items) return;

    const excelData = [
      ["TÁC GIẢ", "TÊN SÁCH", "LƯỢT XEM", "LƯỢT XEM (all)", "SỐ TIỀN THU ĐƯỢC", "SỐ TIỀN (all)"],
      ...((vipData?.items ?? []).map((item) => [
        item.authorName,
        item.bookName,
        item.listens ?? 0,
        item.listensAllTime ?? 0,
        `${(item.money ?? 0).toLocaleString("de-DE")}đ`,
        `${(item.moneyAllTime ?? 0).toLocaleString("de-DE")}đ`,
      ])),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách");

    XLSX.writeFile(workbook, "Sach_VIP.xlsx");
  };

  const handleClickHeaderRetail = (item: any) => {
    if (item.hasOrder) {
      const orderBy = item.key;
      const orderType =
        retailOrder === OrderType.ASC ? OrderType.DESC : OrderType.ASC;

      setRetailOrderBy(orderBy);
      setRetailOrder(orderType);
    }
  };

  const handleClickHeaderVip = (item: any) => {
    if (item.hasOrder) {
      const orderBy = item.key;
      const orderType =
        vipOrder === OrderType.ASC ? OrderType.DESC : OrderType.ASC;

      setVipOrderBy(orderBy);
      setVipOrder(orderType);
    }
  };

  return (
    <div className="flex flex-col gap-[24px]">
      {/* Sách bán lẻ */}
      <div className="p-[24px] bg-white rounded-xl flex flex-col gap-[20px]">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex gap-1 items-center">
              <CardReceiveIcon />
              <p className="text-[18px] text-[#45464E] font-medium">
                Doanh thu sách bán lẻ
              </p>
            </div>
            <div className="flex gap-6 items-end">
              <div>
                <p className="text-[14px] text-[#666]">Trong khoảng lọc</p>
                <p className="text-[28px] text-[#1C1D22] font-medium">
                  {retailData?.totalMoneyAuthor
                    ? retailData?.totalMoneyAuthor.toLocaleString("de-DE")
                    : 0}
                  đ
                </p>
              </div>
              <div>
                <p className="text-[14px] text-[#666]">Tổng tích lũy</p>
                <p className="text-[20px] text-[#1C1D22]">
                  {retailData?.totalMoneyAllTime
                    ? retailData?.totalMoneyAllTime.toLocaleString("de-DE")
                    : 0}
                  đ
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            disabled={!retailData?.items?.length}
            className="focus:outline-none text-white bg-green-700 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 disabled:opacity-50"
            onClick={handleExportToExcelRetail}
          >
            Export Excel
          </button>
        </div>

        <div className="relative overflow-x-auto">
          <table className="w-full text-left rtl:text-right">
            <thead>
              <tr className="border-t border-[#EEF0FA]">
                {retailHeaderTitle?.map((it) => (
                  <th
                    key={it.key}
                    className={clsx(
                      "px-6 py-3 text-[14px] text-[#6E7079] font-normal cursor-pointer",
                      {
                        "bg-[#bac5d4]": it.key === retailOrderBy,
                      }
                    )}
                    onClick={() => handleClickHeaderRetail(it)}
                  >
                    <div className={"flex items-center"}>
                      {it.label}

                      {it.hasOrder && (!retailOrder || it.key !== retailOrderBy) && (
                        <span className="ml-2">
                          <img
                            src={Images.SortIcon.default}
                            alt="sort-icon"
                            className="w-4"
                          />
                        </span>
                      )}

                      {it.hasOrder &&
                        retailOrder === OrderType.ASC &&
                        retailOrderBy === it.key && (
                        <div className="ml-2">
                          <img
                            src={Images.ArrowUpIcon.default}
                            alt="arrow-up-icon"
                            className="w-4"
                          />
                        </div>
                      )}

                      {it.hasOrder &&
                        retailOrder === OrderType.DESC &&
                        retailOrderBy === it.key && (
                        <div className="ml-2">
                          <img
                            src={Images.ArrowDownIcon.default}
                            alt="arrow-down-icon"
                            className="w-4"
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {retailData?.items.map((item, index) => (
                <tr
                  key={index}
                  className="border-t border-[#EEF0FA] text-[14px] text-[#45464E] font-normal"
                >
                  <td className="px-6 py-4">{item.authorName}</td>
                  <td className="px-6 py-4 flex items-center gap-1">
                    {item.thumbnails?.[0] && (
                      <img
                        src={item.thumbnails[0]}
                        alt="book image"
                        className="w-[32px] h-[32px] rounded-[5px]"
                      />
                    )}
                    <p>{item.bookName}</p>
                  </td>
                  <td className="px-6 py-4">{item.purchases ?? 0}</td>
                  <td className="px-6 py-4">
                    {item.money.toLocaleString("de-DE")}đ
                  </td>
                  <td className="px-6 py-4">
                    {(item.moneyAllTime ?? 0).toLocaleString("de-DE")}đ
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PaginationMui
          currentPage={retailMeta?.pageNum ?? 1}
          totalPages={retailMeta?.totalPage ?? 1}
          sizePage={retailMeta?.pageSize ?? 1}
          total={retailMeta?.total ?? 0}
          onPageChange={onRetailPageChange}
          onPageSizeChange={onRetailPageSizeChange}
        />
      </div>

      {/* Sách VIP */}
      <div className="p-[24px] bg-white rounded-xl flex flex-col gap-[20px]">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex gap-1 items-center">
              <CardReceiveIcon />
              <p className="text-[18px] text-[#45464E] font-medium">Doanh thu sách VIP</p>
            </div>
            <div className="flex gap-6 items-end">
              <div>
                <p className="text-[14px] text-[#666]">Trong khoảng lọc</p>
                <p className="text-[28px] text-[#1C1D22] font-medium">
                  {vipData?.totalMoneyAuthor
                    ? vipData?.totalMoneyAuthor.toLocaleString("de-DE")
                    : 0}
                  đ
                </p>
              </div>
              <div>
                <p className="text-[14px] text-[#666]">Tổng tích lũy</p>
                <p className="text-[20px] text-[#1C1D22]">
                  {vipData?.totalMoneyAllTime
                    ? vipData?.totalMoneyAllTime.toLocaleString("de-DE")
                    : 0}
                  đ
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            disabled={!vipData?.items?.length}
            className="focus:outline-none text-white bg-green-700 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 disabled:opacity-50"
            onClick={handleExportToExcelVip}
          >
            Export Excel
          </button>
        </div>

        <div className="relative overflow-x-auto">
          <table className="w-full text-left rtl:text-right">
            <thead>
              <tr className="border-t border-[#EEF0FA]">
                {vipHeaderTitle?.map((it) => (
                  <th
                    key={it.key}
                    className={clsx(
                      "px-6 py-3 text-[14px] text-[#6E7079] font-normal cursor-pointer",
                      {
                        "bg-[#bac5d4]": it.key === vipOrderBy,
                      }
                    )}
                    onClick={() => handleClickHeaderVip(it)}
                  >
                    <div className={"flex items-center"}>
                      {it.label}

                      {it.hasOrder && (!vipOrder || it.key !== vipOrderBy) && (
                        <span className="ml-2">
                          <img
                            src={Images.SortIcon.default}
                            alt="sort-icon"
                            className="w-4"
                          />
                        </span>
                      )}

                      {it.hasOrder &&
                        vipOrder === OrderType.ASC &&
                        vipOrderBy === it.key && (
                        <div className="ml-2">
                          <img
                            src={Images.ArrowUpIcon.default}
                            alt="arrow-up-icon"
                            className="w-4"
                          />
                        </div>
                      )}

                      {it.hasOrder &&
                        vipOrder === OrderType.DESC &&
                        vipOrderBy === it.key && (
                        <div className="ml-2">
                          <img
                            src={Images.ArrowDownIcon.default}
                            alt="arrow-down-icon"
                            className="w-4"
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vipData?.items.map((item, index) => (
                <tr
                  key={index}
                  className="border-t border-[#EEF0FA] text-[14px] text-[#45464E] font-normal"
                >
                  <td className="px-6 py-4">{item.authorName}</td>
                  <td className="px-6 py-4 flex items-center gap-1">
                    {item.thumbnails?.[0] && (
                      <img
                        src={item.thumbnails[0]}
                        alt="book image"
                        className="w-[32px] h-[32px] rounded-[5px]"
                      />
                    )}
                    <p>{item.bookName}</p>
                  </td>
                  <td className="px-6 py-4">{item.listens ?? 0}</td>
                  <td className="px-6 py-4">{item.listensAllTime ?? 0}</td>
                  <td className="px-6 py-4">
                    {item.money.toLocaleString("de-DE")}đ
                  </td>
                  <td className="px-6 py-4">
                    {(item.moneyAllTime ?? 0).toLocaleString("de-DE")}đ
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PaginationMui
          currentPage={vipMeta?.pageNum ?? 1}
          totalPages={vipMeta?.totalPage ?? 1}
          sizePage={vipMeta?.pageSize ?? 1}
          total={vipMeta?.total ?? 0}
          onPageChange={onVipPageChange}
          onPageSizeChange={onVipPageSizeChange}
        />
      </div>
    </div>
  );
};

export default memo(BookRevenue);
